import { getUtcMillisFromTimeInTimeZone } from '@thoughtspot/date-format-util';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
export var AnnouncementType;
(function (AnnouncementType) {
    AnnouncementType["INFO"] = "INFO";
    AnnouncementType["WARNING"] = "WARNING";
    AnnouncementType["ERROR"] = "ERROR";
})(AnnouncementType || (AnnouncementType = {}));
export var StartTimeType;
(function (StartTimeType) {
    StartTimeType["FROM_NOW"] = "from_now";
    StartTimeType["CUSTOM"] = "custom";
})(StartTimeType || (StartTimeType = {}));
const announcementToBannerMap = {
    [AnnouncementType.INFO]: BannerAlertType.INFO,
    [AnnouncementType.WARNING]: BannerAlertType.WARNING,
    [AnnouncementType.ERROR]: BannerAlertType.ERROR,
};
const apiToAnnouncementMap = {
    information: AnnouncementType.INFO,
    info: AnnouncementType.INFO,
    warning: AnnouncementType.WARNING,
    failure: AnnouncementType.ERROR,
    error: AnnouncementType.ERROR,
};
const announcementToApiMap = {
    [AnnouncementType.INFO]: 'INFO',
    [AnnouncementType.WARNING]: 'WARNING',
    [AnnouncementType.ERROR]: 'ERROR',
};
export const getBannerType = (type) => announcementToBannerMap[type] ?? BannerAlertType.INFO;
export const getAnnouncementTypeFromApi = (type) => apiToAnnouncementMap[type?.toLowerCase()] ?? AnnouncementType.INFO;
export const getApiTypeFromAnnouncement = (type) => announcementToApiMap[type] ?? 'INFO';
export const formatDateTimeToUtcMillis = (date, hour, minute, timezone) => {
    return getUtcMillisFromTimeInTimeZone(date.getFullYear(), date.getMonth(), date.getDate(), parseInt(hour, 10), parseInt(minute, 10), timezone);
};
const roundMinuteToNearestFive = (minute) => {
    const rounded = Math.round(minute / 5) * 5;
    return Math.min(rounded, 55);
};
export const parseApiDateTime = (epochMillis, timezone) => {
    const date = new Date(epochMillis);
    const timezoneOffsetDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const rawMinute = timezoneOffsetDate.getMinutes();
    const roundedMinute = roundMinuteToNearestFive(rawMinute);
    return {
        date: timezoneOffsetDate,
        hour: timezoneOffsetDate
            .getHours()
            .toString()
            .padStart(2, '0'),
        minute: roundedMinute.toString().padStart(2, '0'),
    };
};
export const generateHourOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        const hourString = hour.toString().padStart(2, '0');
        options.push({
            id: hourString,
            label: hourString,
        });
    }
    return options;
};
export const generateMinuteOptions = () => {
    const options = [];
    for (let minute = 0; minute < 60; minute += 5) {
        const minuteString = minute.toString().padStart(2, '0');
        options.push({
            id: minuteString,
            label: minuteString,
        });
    }
    return options;
};
export const isValidUrl = (url) => {
    try {
        const parsedUrl = new URL(url);
        return parsedUrl !== null;
    }
    catch (error) {
        return false;
    }
};
export var BannerStatus;
(function (BannerStatus) {
    BannerStatus["ACTIVE"] = "ACTIVE";
    BannerStatus["INACTIVE"] = "INACTIVE";
    BannerStatus["SCHEDULED"] = "SCHEDULED";
})(BannerStatus || (BannerStatus = {}));
export const getBannerStatus = (bannerConfig) => {
    if (!bannerConfig?.content ||
        !bannerConfig.startTime ||
        !bannerConfig.endTime)
        return BannerStatus.INACTIVE;
    const now = new Date().getTime();
    const startTime = bannerConfig.startTime;
    const endTime = bannerConfig.endTime;
    if (now < startTime)
        return BannerStatus.SCHEDULED;
    if (now > endTime)
        return BannerStatus.INACTIVE;
    return BannerStatus.ACTIVE;
};
export const buildMixPanelProperties = (bannerConfig, bannerLevel, includeTimeFields = true) => {
    if (!bannerConfig)
        return {};
    const properties = {
        announcementType: bannerConfig.type,
        hasLink: !!bannerConfig.linkText && !!bannerConfig.linkUrl,
        contentLength: bannerConfig.content?.length ?? 0,
        urlLength: bannerConfig.linkUrl?.length ?? 0,
        urlTextLength: bannerConfig.linkText?.length ?? 0,
        orgTab: bannerLevel,
    };
    if (includeTimeFields) {
        properties.startTime = bannerConfig.startTime;
        properties.endTime = bannerConfig.endTime;
    }
    return properties;
};
export const validateStartTime = (formData, timezone, isCreateMode, t) => {
    if (formData.startTimeType !== StartTimeType.CUSTOM) {
        return null;
    }
    if (!formData.customStartDate) {
        return t('admin.home.announcement.validation.emptyStartTime');
    }
    if (formData.customStartDate && isCreateMode) {
        const startTime = formatDateTimeToUtcMillis(formData.customStartDate, formData.customStartTime.hour, formData.customStartTime.minute, timezone);
        const currentTime = new Date().getTime();
        if (startTime < currentTime) {
            return t('admin.home.announcement.validation.startTimeBeforeCurrentTime');
        }
    }
    return null;
};
export const validateEndTime = (formData, timezone, t) => {
    if (!formData.endDate) {
        return t('admin.home.announcement.validation.emptyEndTime');
    }
    const endTime = formatDateTimeToUtcMillis(formData.endDate, formData.endTime.hour, formData.endTime.minute, timezone);
    const currentTime = new Date().getTime();
    if (endTime <= currentTime) {
        return t('admin.home.announcement.validation.endTimeBeforeCurrentTime');
    }
    if (formData.startTimeType === StartTimeType.CUSTOM &&
        formData.customStartDate) {
        const startTime = formatDateTimeToUtcMillis(formData.customStartDate, formData.customStartTime.hour, formData.customStartTime.minute, timezone);
        if (endTime <= startTime) {
            return t('admin.home.announcement.validation.endTimeBeforeStartTime');
        }
    }
    return null;
};
//# sourceMappingURL=banner-utils.js.map